import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, BeziehungKonditionen, KonditionAuswahl,
              KonditionenAnBeziehung, KonditionsFelder, Zuordnung } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { formatKondition } from "../format.js";
import "./KundeKonditionen.css";

/** Nur die drei Klientenmethoden, die dieser Abschnitt wirklich braucht — so lässt er sich
 *  im Test mit drei vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen (Muster aus
 *  KundeStammdaten.tsx). */
export type KonditionenClient = Pick<ApiClient,
  "listSelectablePaymentTerms" | "getRelationTerms" | "setRelationTerms">;

export type KundeKonditionenProps = {
  client: KonditionenClient;
  /** Die Kundenbeziehungen aus der Akte. Die Konditionen hängen an IHNEN, nicht an der
   *  Firma: Dieselbe Firma kann mit zwei Konzernunternehmen Verschiedenes vereinbart
   *  haben (Spezifikation, Abschnitt „Abweichungen vom Fundament"). */
  relations: Zuordnung[];
  /**
   * Über diesem Abschnitt steht ein FREMDES Panel — das Stammdaten-Formular (Fixrunde 1,
   * 07.09.2026).
   *
   * Der Abschnitt steht unter den Stammdaten und liegt damit hinter deren Panel. Sein
   * „Ändern" ist dort nicht anklickbar, und ein Knopf, den niemand drücken kann, gehört
   * gesperrt — nicht zuletzt, weil in jsdom nichts den Klick abfängt und ein Test sonst
   * hinter das Panel griffe.
   *
   * Wahlfrei, weil es nicht die Sache dieses Abschnitts ist: Er kennt seinen Platz nicht.
   * Wer ihn woanders einhängt, wo kein Panel darüberliegt, lässt sie weg.
   */
  verdeckt?: boolean;
};

/** Alle Feldnamen, die eine 422 dieser Maske nennen kann — die vier aus `validateTerms`. */
type FeldName = "paymentTermId" | "netDays" | "discountPercent" | "discountDays";

/**
 * Die drei Bedienformen. Die XOR-Regel des Servers („Verweis ODER eigene Werte") wird hier
 * zur BEDIENFORM: Man wählt, was gelten soll, statt beides einzutippen und danach zu
 * erfahren, dass das nicht geht. Die 422 auf `paymentTermId` bleibt als Riegel bestehen —
 * sie ist nur nicht mehr der übliche Weg, von ihr zu erfahren.
 */
type Bedienform = "STANDARD" | "EIGEN" | "KEINE";

/**
 * Der offene Ändern-Dialog. `version` ist die Versionsbasis der KUNDENBEZIEHUNG, mit der
 * gespeichert wird — sie kommt beim Öffnen aus dem zuletzt bekannten Stand und rückt nach
 * einem Konflikt bewusst nach (siehe eigeneEingabenBehalten).
 *
 * Die drei Zahlenfelder stehen als Zeichenketten da, damit ein geleertes Feld erst beim
 * Absenden zu `null` wird — sonst liesse sich eine einmal eingetragene Zahl nicht mehr
 * entfernen.
 */
type DialogStand = {
  relationId: string;
  /** Für die Überschrift des Dialogs: Kürzel und Name des Konzernunternehmens. */
  titel: string;
  version: number;
  form: Bedienform;
  paymentTermId: string;
  netDays: string;
  discountPercent: string;
  discountDays: string;
};

/**
 * Ob zwei Dialogstände dieselben EINGABEN tragen. Bewusst ohne `version` und `titel`:
 * Die Versionsbasis rückt nach einem Konflikt nach (siehe eigeneEingabenBehalten), ohne
 * dass der Benutzer etwas getippt hätte — sie als Änderung zu zählen, machte aus einer
 * Konfliktauflösung einen ungespeicherten Stand.
 */
function eingabenGleich(a: DialogStand, b: DialogStand): boolean {
  return a.form === b.form && a.paymentTermId === b.paymentTermId
    && a.netDays === b.netDays && a.discountPercent === b.discountPercent
    && a.discountDays === b.discountDays;
}

type Konflikt = { von: string; wann: string; fremd: BeziehungKonditionen | null };

const LEERE_EINGABEN = { paymentTermId: "", netDays: "", discountPercent: "", discountDays: "" };

/**
 * Welche Bedienform ein eingetragener Stand ist. Genau die Fallunterscheidung, die auch
 * `resolvePaymentTerm` in `domain` trifft — nur ohne die Werte aufzulösen: Ein Verweis ist
 * ein Verweis, ein eigener Wert sind eigene Werte, nichts ist nichts.
 */
function formAus(terms: BeziehungKonditionen): Bedienform {
  if (terms.paymentTermId !== null) return "STANDARD";
  if (terms.netDays !== null || terms.discountPercent !== null
    || terms.discountDays !== null) return "EIGEN";
  return "KEINE";
}

/** Eine Zahl als Feldwert — leer bleibt leer, damit ein nicht vereinbarter Wert nicht als
 *  „0" im Feld steht und beim nächsten Speichern eine Null vereinbarte. */
function alsFeldwert(zahl: number | null): string {
  return zahl === null ? "" : String(zahl);
}

function standAus(
  relationId: string, titel: string, stand: KonditionenAnBeziehung,
): DialogStand {
  return {
    relationId,
    titel,
    version: stand.terms.version,
    form: formAus(stand.terms),
    paymentTermId: stand.terms.paymentTermId ?? "",
    netDays: alsFeldwert(stand.terms.netDays),
    discountPercent: alsFeldwert(stand.terms.discountPercent),
    discountDays: alsFeldwert(stand.terms.discountDays),
  };
}

/**
 * Eine Eingabe als Zahl — oder `"KEINE_ZAHL"`, wenn dort etwas steht, das keine ist.
 *
 * Das ist KEINE zweite Fachprüfung neben `validateTerms` (Bereiche, Nachkommastellen und
 * die XOR-Regel bleiben ausschliesslich dort): Es ist die Umwandlung von Text in Zahl, und
 * die kann scheitern. Ohne diese Stelle ginge aus „abc" ein `NaN` hinaus, das
 * `JSON.stringify` still zu `null` macht — die Maske meldete nichts, und statt einer
 * Fehlermeldung stünde hinterher „keine Kondition vereinbart" da.
 */
const KEINE_ZAHL = Symbol("keine Zahl");

function alsZahl(eingabe: string): number | null | typeof KEINE_ZAHL {
  const roh = eingabe.trim();
  if (roh === "") return null;
  // Komma als Dezimaltrennzeichen ist die deutsche Schreibweise; `Number` kennt nur den Punkt.
  const zahl = Number(roh.replace(",", "."));
  return Number.isFinite(zahl) ? zahl : KEINE_ZAHL;
}

/**
 * `conflict.current` kommt als unknown über die Leitung (die API kann seine Gestalt im
 * Schema nicht versprechen, siehe openapi.ts). Tatsächlich liefert der Fehlerhandler die
 * volle Kundenbeziehung; hier wird das geprüft statt blind behauptet — fehlt die Gestalt,
 * zeigt der Konfliktkasten nur die Meldung.
 */
function alsBeziehung(wert: unknown): BeziehungKonditionen | null {
  if (typeof wert !== "object" || wert === null) return null;
  const b = wert as Partial<BeziehungKonditionen>;
  if (typeof b.id !== "string" || typeof b.version !== "number") return null;
  return wert as BeziehungKonditionen;
}

const KONFLIKT_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

function formatKonfliktZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  return `${KONFLIKT_ZEIT.format(zeitpunkt)} Uhr`;
}

/**
 * Der Abschnitt „Zahlungskonditionen" unter den Stammdaten — je Kundenbeziehung eine Zeile.
 *
 * Angezeigt wird die AUFGELÖSTE Kondition (`resolved`), also das, was tatsächlich gilt:
 * Bei einem Verweis sind das die Werte des Stammsatzes, und eine Änderung am Standard wirkt
 * damit sofort hier durch — genau der Zweck der Konstruktion (Spezifikation). Der
 * eingetragene Stand (`terms`) steht daneben nur als Quellenangabe und ist die Grundlage
 * des Ändern-Dialogs.
 *
 * Die XOR-Regel erscheint als Bedienform, nicht als Fehlermeldung: Wer „Standard aus der
 * Liste" wählt, sieht die eigenen Felder gar nicht mehr, und der Wechsel leert sie. Die
 * 422 des Servers bleibt trotzdem verdrahtet — sie fängt, was diese Maske nicht abfängt
 * (Bereiche, Nachkommastellen, Skonto-Paar).
 */
export function KundeKonditionen(p: KundeKonditionenProps): ReactElement {
  /** Der zuletzt bekannte Stand je Beziehung — Quelle für die Zeile UND für die Version
   *  beim Speichern. Nach einem Erfolg steht hier die ANTWORT mit ihrer neuen Version;
   *  mit der alten scheiterte das nächste Speichern sofort am Versionsvergleich. */
  const [staende, setStaende] = useState<Record<string, KonditionenAnBeziehung>>({});
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [standards, setStandards] = useState<KonditionAuswahl[]>([]);
  const [standardFehler, setStandardFehler] = useState<string | null>(null);

  const [dialog, setDialog] = useState<DialogStand | null>(null);
  const [feldFehler, setFeldFehler] = useState<Partial<Record<FeldName, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const { client, relations } = p;
  const getRelationTerms = client.getRelationTerms;
  const listStandards = client.listSelectablePaymentTerms;
  // Die Kennungen als eine Zeichenkette: Der Ladeeffekt darf nicht bei jedem Neuzeichnen
  // erneut laufen, nur weil `relations` ein neues Feld-Objekt ist.
  const relationIds = relations.map((r) => r.id).join(",");

  useEffect(() => {
    const ids = relationIds === "" ? [] : relationIds.split(",");
    if (ids.length === 0) {
      setStaende({});
      return;
    }
    let abgebrochen = false;
    setLadeFehler(null);
    Promise.all(ids.map(async (id) => [id, await getRelationTerms(id)] as const))
      .then((paare) => {
        if (abgebrochen) return;
        setStaende(Object.fromEntries(paare));
      })
      .catch((fehler: unknown) => {
        if (abgebrochen) return;
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Die Zahlungskonditionen konnten nicht geladen werden.");
      });
    return () => { abgebrochen = true; };
  }, [relationIds, getRelationTerms]);

  // Die Standards hängen an keiner Beziehung — sie werden einmal geholt. Scheitert das,
  // bleibt der Abschnitt lesbar und nur die Auswahl „Standard aus der Liste" entfällt; ein
  // Hinweis sagt es, statt stillschweigend eine leere Liste anzubieten (Muster: die Rollen
  // in KundeAnsprechpartner.tsx).
  useEffect(() => {
    let abgebrochen = false;
    listStandards()
      .then((antwort) => { if (!abgebrochen) setStandards(antwort.items); })
      .catch(() => {
        if (!abgebrochen) {
          setStandardFehler("Die gepflegten Zahlungskonditionen konnten nicht geladen "
            + "werden — es lässt sich gerade kein Standard auswählen.");
        }
      });
    return () => { abgebrochen = true; };
  }, [listStandards]);

  // 2a-Kleinpunkt: Der Browser-Standarddialog beim ECHTEN Verlassen (Tab zu, neu laden,
  // App-Wechsel). Verglichen wird mit der Ausgangslage BEIM ÖFFNEN, die sich jederzeit aus
  // dem zuletzt bekannten Stand nachbauen lässt (`standAus` — dieselbe Stelle, aus der
  // `oeffnen` den Dialog befüllt); ein zweiter Zustand daneben ginge sonst mit der Zeit
  // auseinander. Ein bloss geöffneter, unberührter Dialog löst nichts aus.
  const dialogStand = dialog === null ? undefined : staende[dialog.relationId];
  const dialogGeaendert = dialog !== null && dialogStand !== undefined
    && !eingabenGleich(dialog, standAus(dialog.relationId, dialog.titel, dialogStand));
  useBeforeUnload(dialogGeaendert);

  /**
   * Woher die geltende Kondition kommt — die EINE Stelle, die Verweis und eigene Werte
   * auseinanderhält. Sie liest `resolved.source` (aus `resolvePaymentTerm` in `domain`),
   * nicht die eingetragenen Spalten: Die Auflösung entscheidet, nicht die Ansicht.
   */
  function quelle(stand: KonditionenAnBeziehung): string | null {
    if (stand.resolved === null) return null;
    if (stand.resolved.source === "CUSTOM") return "eigene Werte";
    const name = standards.find((s) => s.id === stand.terms.paymentTermId)?.name;
    return name === undefined ? "Standard" : `Standard „${name}“`;
  }

  function oeffnen(r: Zuordnung): void {
    const stand = staende[r.id];
    if (stand === undefined) return;
    setFeldFehler({});
    setMeldung(null);
    setKonflikt(null);
    setDialog(standAus(r.id, `${r.shortCode} · ${r.companyName}`, stand));
  }

  /** Das Panel zumachen und alles abräumen, was zu ihm gehörte — der Weg NACH einem
   *  Entscheid (Erfolg oder bestätigtes Abbrechen), nicht der Ausgang selbst. */
  function schliessen(): void {
    setDialog(null);
    setFeldFehler({});
    setKonflikt(null);
  }

  /**
   * Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel", 06.09.2026):
   * kein Ausgang unter laufendem Schreiben, Rückfrage bei ungespeicherten Eingaben. Knopf
   * und Escape gehen durch diese Funktion — der Rahmen ruft sie ebenso wie die Fußleiste.
   */
  function abbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(dialogGeaendert)) return;
    schliessen();
  }

  /**
   * Der Wechsel der Bedienform leert die Eingaben der jeweils ANDEREN Formen.
   *
   * Ohne dieses Leeren stünde nach „eigene Werte → Standard aus der Liste" beides im
   * Entwurf, und der Server wiese das mit genau der 422 ab, die diese Bedienform vermeiden
   * soll — der Benutzer sähe einen Fehler für etwas, das er auf dem Bildschirm gar nicht
   * mehr sieht.
   */
  function formWechseln(form: Bedienform): void {
    setFeldFehler({});
    setDialog((d) => (d === null ? d : { ...d, ...LEERE_EINGABEN, form }));
  }

  /** Der Rumpf für den Schreibweg — genau eine der drei Formen ist gefüllt. */
  function eingabeAus(d: DialogStand): KonditionsFelder | null {
    if (d.form === "KEINE") {
      return { paymentTermId: null, netDays: null, discountPercent: null, discountDays: null };
    }
    if (d.form === "STANDARD") {
      return {
        paymentTermId: d.paymentTermId === "" ? null : d.paymentTermId,
        netDays: null, discountPercent: null, discountDays: null,
      };
    }
    const netDays = alsZahl(d.netDays);
    const discountPercent = alsZahl(d.discountPercent);
    const discountDays = alsZahl(d.discountDays);
    const unlesbar: Partial<Record<FeldName, string>> = {};
    if (netDays === KEINE_ZAHL) unlesbar.netDays = "Bitte eine Zahl eintragen.";
    if (discountPercent === KEINE_ZAHL) {
      unlesbar.discountPercent = "Bitte eine Zahl eintragen.";
    }
    if (discountDays === KEINE_ZAHL) unlesbar.discountDays = "Bitte eine Zahl eintragen.";
    if (netDays === KEINE_ZAHL || discountPercent === KEINE_ZAHL
      || discountDays === KEINE_ZAHL) {
      setFeldFehler(unlesbar);
      return null;
    }
    return { paymentTermId: null, netDays, discountPercent, discountDays };
  }

  async function speichern(): Promise<void> {
    const d = dialog;
    if (d === null) return;
    setFeldFehler({});
    setMeldung(null);
    const eingabe = eingabeAus(d);
    if (eingabe === null) return; // unlesbare Zahl — der Feldfehler steht schon
    setSpeichert(true);
    try {
      const antwort = await client.setRelationTerms(d.relationId, d.version, eingabe);
      // Die ANTWORT übernehmen, nicht den eigenen Entwurf: Sie trägt die neue Version
      // (sonst scheiterte das nächste Speichern am Versionsvergleich) und die frisch
      // aufgelöste Kondition.
      setStaende((alt) => ({ ...alt, [d.relationId]: antwort }));
      setDialog(null);
      setKonflikt(null);
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.status === 422 && fehler.fields !== undefined
          && fehler.fields.length > 0) {
        const neu: Partial<Record<FeldName, string>> = {};
        let unzuordenbar = false;
        for (const f of fehler.fields) {
          if (f.field === "paymentTermId" || f.field === "netDays"
            || f.field === "discountPercent" || f.field === "discountDays") {
            if (neu[f.field] === undefined) neu[f.field] = f.message;
          } else {
            unzuordenbar = true;
          }
        }
        setFeldFehler(neu);
        // Nennt die 422 ein Feld, das diese Maske nicht zeigt, darf der Fehler nicht
        // unsichtbar bleiben — dann zusätzlich die Sammelmeldung.
        if (unzuordenbar) setMeldung(fehler.message);
      } else if (fehler instanceof ApiError && fehler.status === 409
          && fehler.conflict !== undefined) {
        setKonflikt({
          von: fehler.conflict.changedBy,
          wann: fehler.conflict.changedAt,
          fremd: alsBeziehung(fehler.conflict.current),
        });
      } else if (fehler instanceof ApiError) {
        // Deckt auch status 0 (NETZWERKFEHLER) ab: Meldung zeigen, die Eingaben bleiben
        // stehen, ein zweiter Versuch ist sofort möglich.
        setMeldung(fehler.message);
      } else {
        setMeldung("Unerwarteter Fehler beim Speichern. Bitte erneut versuchen.");
      }
    } finally {
      setSpeichert(false);
    }
  }

  /** Konfliktauflösung 1: den fremden Stand übernehmen — die eigene Eingabe weicht ihm auf
   *  ausdrücklichen Klick, nie stillschweigend. Neu geladen wird, weil die 409 nur den
   *  eingetragenen Stand trägt; die GELTENDE Kondition dazu kennt nur der Server. */
  async function fremdenStandUebernehmen(): Promise<void> {
    const d = dialog;
    if (d === null) return;
    setSpeichert(true);
    try {
      const frisch = await client.getRelationTerms(d.relationId);
      setStaende((alt) => ({ ...alt, [d.relationId]: frisch }));
      setDialog(standAus(d.relationId, d.titel, frisch));
      setKonflikt(null);
      setFeldFehler({});
    } catch (fehler) {
      setMeldung(fehler instanceof ApiError ? fehler.message
        : "Der aktuelle Stand konnte nicht geladen werden.");
    } finally {
      setSpeichert(false);
    }
  }

  /** Konfliktauflösung 2: die eigene Eingabe behalten. Die Versionsbasis rückt auf den
   *  Serverstand vor — das nächste Speichern überschreibt den fremden Stand dann BEWUSST
   *  (der Kasten hat gesagt, dass es einen gibt). */
  function eigeneEingabenBehalten(): void {
    const fremd = konflikt?.fremd ?? null;
    if (fremd !== null) {
      setDialog((d) => (d === null ? d : { ...d, version: fremd.version }));
    }
    setKonflikt(null);
  }

  const waehlbareStandards = dialog === null ? [] : standards.filter(
    (s) => s.active || s.id === dialog.paymentTermId);

  return (
    <section className="kk" aria-label="Zahlungskonditionen">
      <h3 className="kk__titel">Zahlungskonditionen</h3>

      {meldung !== null && dialog === null && (
        <div className="kk__meldung" role="alert">{meldung}</div>
      )}
      {ladeFehler !== null && <div className="kk__meldung" role="alert">{ladeFehler}</div>}

      {relations.length === 0 && (
        <p className="kk__hinweis">Dieser Kunde ist noch keinem Unternehmen zugeordnet.</p>
      )}

      <ul className="kk__liste">
        {relations.map((r) => {
          const stand = staende[r.id];
          return (
            <li key={r.id} className="kk__zeile">
              <span className="kk__chip">{r.shortCode}</span>
              {stand === undefined ? (
                <span className="kk__wert">Lädt …</span>
              ) : (
                <>
                  <span className="kk__wert">{formatKondition(stand.resolved)}</span>
                  {quelle(stand) !== null && (
                    <span className="kk__quelle">{quelle(stand)}</span>
                  )}
                </>
              )}
              <button
                type="button"
                className="kk__knopf"
                aria-label={`Zahlungskondition für ${r.companyName} ändern`}
                disabled={stand === undefined || dialog !== null || p.verdeckt === true}
                onClick={() => oeffnen(r)}
              >
                Ändern
              </button>
            </li>
          );
        })}
      </ul>

      {dialog !== null && (
        <Modal titel={`Zahlungskondition — ${dialog.titel}`} onAbbrechen={abbrechen}
          abbrechenGesperrt={speichert}>
          {/* Der Inhalt trägt seinen eigenen senkrechten Abstand: Der gemeinsame Rahmen
              staffelt seine Kinder nicht, damit jede Maske ihr Raster selbst bestimmt. */}
          <div className="kk__dialoginhalt">
            {meldung !== null && <div className="kk__meldung" role="alert">{meldung}</div>}

            {konflikt !== null && (
              <div className="kk__konflikt" role="alert">
                <p className="kk__konflikt-titel">
                  {konflikt.von} hat diese Zahlungskondition inzwischen geändert
                  ({formatKonfliktZeit(konflikt.wann)}). Deine Eingaben sind nicht verloren —
                  entscheide, welcher Stand gilt.
                </p>
                <div className="kk__dialog-aktionen">
                  <button type="button" className="kk__knopf" disabled={speichert}
                    onClick={() => { void fremdenStandUebernehmen(); }}>
                    Fremden Stand übernehmen
                  </button>
                  <button type="button" className="kk__knopf" disabled={speichert}
                    onClick={eigeneEingabenBehalten}>
                    Meine Eingaben behalten
                  </button>
                </div>
              </div>
            )}

            <fieldset className="kk__formwahl">
              <legend className="kk__formwahl-titel">Was soll gelten?</legend>
              <label className="kk__formwahl-eintrag">
                <input type="radio" name="kk-form" checked={dialog.form === "STANDARD"}
                  onChange={() => formWechseln("STANDARD")} />
                <span>Standard aus der Liste</span>
              </label>
              <label className="kk__formwahl-eintrag">
                <input type="radio" name="kk-form" checked={dialog.form === "EIGEN"}
                  onChange={() => formWechseln("EIGEN")} />
                <span>Eigene Werte</span>
              </label>
              <label className="kk__formwahl-eintrag">
                <input type="radio" name="kk-form" checked={dialog.form === "KEINE"}
                  onChange={() => formWechseln("KEINE")} />
                <span>Keine Kondition</span>
              </label>
            </fieldset>

            {dialog.form === "STANDARD" && (
              <div className="kk__auswahl">
                <label htmlFor="kk-standard" className="kk__label">Zahlungskondition</label>
                {standardFehler !== null && (
                  <p className="kk__hinweis" role="status">{standardFehler}</p>
                )}
                <select id="kk-standard" className="kk__select" value={dialog.paymentTermId}
                  onChange={(e) => setDialog((d) => (d === null ? d
                    : { ...d, paymentTermId: e.target.value }))}>
                  <option value="">Bitte wählen …</option>
                  {waehlbareStandards.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.active ? s.name : `${s.name} (stillgelegt)`}
                    </option>
                  ))}
                </select>
                {feldFehler.paymentTermId !== undefined && (
                  <span className="kk__feldfehler">{feldFehler.paymentTermId}</span>
                )}
              </div>
            )}

            {dialog.form === "EIGEN" && (
              <div className="kk__felder">
                <Feld id="kk-netDays" label="Zahlungsziel (Tage)" bearbeitbar
                  wert={dialog.netDays}
                  onChange={(wert) => setDialog((d) => (d === null ? d : { ...d, netDays: wert }))}
                  {...(feldFehler.netDays !== undefined ? { fehler: feldFehler.netDays } : {})}
                />
                <Feld id="kk-discountPercent" label="Skonto (%)" bearbeitbar
                  wert={dialog.discountPercent}
                  onChange={(wert) => setDialog((d) => (d === null ? d
                    : { ...d, discountPercent: wert }))}
                  {...(feldFehler.discountPercent !== undefined
                    ? { fehler: feldFehler.discountPercent } : {})}
                />
                <Feld id="kk-discountDays" label="Skontofrist (Tage)" bearbeitbar
                  wert={dialog.discountDays}
                  onChange={(wert) => setDialog((d) => (d === null ? d
                    : { ...d, discountDays: wert }))}
                  {...(feldFehler.discountDays !== undefined
                    ? { fehler: feldFehler.discountDays } : {})}
                />
              </div>
            )}

            {dialog.form === "KEINE" && (
              <p className="kk__hinweis">
                Für diese Kundenbeziehung gilt dann keine vereinbarte Zahlungskondition.
              </p>
            )}
          </div>

          <ModalFuss speichernText="Speichern" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
